import axios from 'axios'

// Kiosk devices authenticate separately from staff users (src/clients/ApiClient.js).
// Token keys are deliberately distinct so a kiosk session and a staff session can
// coexist on the same machine without clobbering each other.
const KIOSK_ACCESS_TOKEN_KEY = 'kioskAccessToken'
const KIOSK_REFRESH_TOKEN_KEY = 'kioskRefreshToken'

const apiKioskClient = axios.create({
    baseURL: process.env.REACT_APP_API_DTR_URL,
    timeout: 60000,
    headers: {
        'Content-Type': 'application/json'
    }
})

apiKioskClient.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem(KIOSK_ACCESS_TOKEN_KEY)
        if (token) {
            config.headers.Authorization = `Bearer ${token}`
        }
        return config
    },
    (error) => {
        return Promise.reject(error)
    }
)

// Plain axios, not apiKioskClient — refreshing must not go through the request
// interceptor above (it would attach the very access token that just expired)
// and must not be interceptable by the 401 handler below.
// Kiosk refresh tokens are single-use and rotate on every call, so concurrent
// 401s must share one in-flight refresh rather than each spending the token.
let inFlightRefresh = null

export const refreshKioskSession = () => {
    if (!inFlightRefresh) {
        inFlightRefresh = (async () => {
            const refreshToken = localStorage.getItem(KIOSK_REFRESH_TOKEN_KEY)
            if (!refreshToken) {
                throw new Error('No kiosk refresh token available')
            }

            // Backend uses snake_case for both the login/refresh request body and
            // response (confirmed live: POST /kiosk/auth/login returns
            // { access_token, refresh_token, kiosk }), unlike the camelCase used by
            // admin kiosk-management endpoints.
            const response = await axios.post(`${process.env.REACT_APP_API_DTR_URL}/kiosk/auth/refresh`, {
                refresh_token: refreshToken
            })

            localStorage.setItem(KIOSK_ACCESS_TOKEN_KEY, response.data.access_token)
            localStorage.setItem(KIOSK_REFRESH_TOKEN_KEY, response.data.refresh_token)
            return response.data.access_token
        })().finally(() => {
            inFlightRefresh = null
        })
    }
    return inFlightRefresh
}

export const clearKioskSession = () => {
    localStorage.removeItem(KIOSK_ACCESS_TOKEN_KEY)
    localStorage.removeItem(KIOSK_REFRESH_TOKEN_KEY)
}

apiKioskClient.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config
        const status = error.response?.status
        const isAuthEndpoint = originalRequest?.url?.startsWith('/kiosk/auth/')

        if (status === 401 && originalRequest && !isAuthEndpoint && !originalRequest._kioskRefreshed) {
            originalRequest._kioskRefreshed = true
            try {
                const accessToken = await refreshKioskSession()
                originalRequest.headers.Authorization = `Bearer ${accessToken}`
                return apiKioskClient(originalRequest)
            } catch (refreshError) {
                clearKioskSession()
                return Promise.reject(error)
            }
        }

        return Promise.reject(error)
    }
)

export default apiKioskClient
